// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A table component for displaying exchange order information with filtering.
 */
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { ExchangeOrderFilter } from '@/exchange-order/api/types';
import { ExchangeOrderTableRow } from '@/exchange-order/components/exchange-order-table-row';
import {
  ExchangeOrderSortProperty,
  useExchangeOrders,
} from '@/exchange-order/hooks/use-exchange-orders';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Props for the ExchangeOrderTable component.
 */
interface Props {
  filter?: ExchangeOrderFilter;
  sort: Sort<ExchangeOrderSortProperty>;
  onSort: (property: ExchangeOrderSortProperty) => void;
  onPageChange: (pageNumber: number) => void;
}

/**
 * A table component for displaying exchange order information with filtering.
 */
export function ExchangeOrderTable(props: Props) {
  const { exchangeOrders, resultCount } = useExchangeOrders({
    filter: props.filter,
  });

  const pageNumber = props.filter?.page ?? 1;
  const pageSize = props.filter?.pageSize ?? 20;

  const onPageChange = props.onPageChange;

  /**
   * Table sort helpers for managing sort indicators.
   */
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  /**
   * Creates a sort handler for a specific column.
   */
  const createSortHandler = useCallback(
    (property: ExchangeOrderSortProperty) => () => {
      props.onSort(property);
    },
    [props]
  );

  /**
   * Sort exchange orders client-side based on current sort configuration.
   */
  const sortedExchangeOrders = useMemo(() => {
    const statusOrder = ['draft', 'in_progress', 'complete', 'cancelled'];

    return [...exchangeOrders].sort((a, b) => {
      let comparison = 0;

      if (props.sort.property === 'eoNumber') {
        comparison = a.exchangeNumber.localeCompare(b.exchangeNumber);
      } else if (props.sort.property === 'status') {
        const aIndex = statusOrder.indexOf(a.status);
        const bIndex = statusOrder.indexOf(b.status);
        comparison = aIndex - bIndex;
      }

      return props.sort.direction === 'desc' ? -comparison : comparison;
    });
  }, [exchangeOrders, props.sort]);

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && exchangeOrders.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [exchangeOrders.length, pageNumber, onPageChange]);

  if (exchangeOrders.length === 0) {
    return (
      <EmptyState title="No exchanges found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking exchanges in Hardfin, add a new exchange
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                width="25%"
                sortDirection={getSortDirection('eoNumber')}
              >
                <TableSortLabel
                  active={isLabelActive('eoNumber')}
                  direction={getLabelDirection('eoNumber')}
                  onClick={createSortHandler('eoNumber')}
                >
                  References
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%">From/To location</TableCell>
              <TableCell width="30%">Items</TableCell>
              <TableCell width="20%" sortDirection={getSortDirection('status')}>
                <TableSortLabel
                  active={isLabelActive('status')}
                  direction={getLabelDirection('status')}
                  onClick={createSortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedExchangeOrders.map((exchangeOrder) => (
              <ExchangeOrderTableRow
                key={exchangeOrder.id}
                exchangeOrder={exchangeOrder}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{ singular: 'exchange', includeCount: false }}
      />
    </>
  );
}
